<template>
  <PublicPageShell>
    <!-- Background: grid + vignette + glow -->
    <div aria-hidden="true" class="pointer-events-none absolute inset-0">
    </div>

    <!-- HERO -->
    <section class="scio-snap-section scio-snap-hero relative z-10 order-1">
      <div class="mx-auto max-w-7xl px-4 pb-0 pt-8 sm:px-6 sm:pb-0 sm:pt-10">
        <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-12 lg:gap-8">
          <!-- Copy -->
          <div class="lg:col-span-7">
            <!-- Badge with p1 -->
            <span
              class="inline-flex items-center gap-2 scio-text-meta scio-weight-semibold tracking-wide"
              :style="{ color: 'var(--color-scio-yellow-p1)' }">
              Why Scio
              <span class="h-1 w-1 rounded-full bg-neutral-400"></span>
              Clarity before commitment
            </span>

            <h1 class="mt-5 text-4xl/tight sm:text-5xl/tight scio-weight-heading text-neutral-50">
              A decision model for complex programmes.
              <span class="block" :style="{ color: 'var(--color-scio-yellow-p2)' }">
                Know before you commit.
              </span>
            </h1>

            <p class="mt-6 scio-body-1 sm:scio-text-lead text-neutral-200 max-w-3xl">
              Scio connects the data you already have with domain expertise and predictive logic. It turns early-stage
              uncertainty into quantified scenarios, showing what will break first and which levers can change the
              outcome.
            </p>

            <!-- Mini KPI row (p3 highlight) -->
            <div class="mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-5">
              <div>
                <div class="scio-text-meta scio-weight-semibold text-neutral-300">Decision horizon</div>
                <div class="mt-2 scio-text-lead scio-weight-heading text-neutral-50">
                  Earlier
                  <span :style="{ color: 'var(--color-scio-yellow-p3)' }">visibility</span>
                </div>
                <p class="mt-2 scio-text-body text-neutral-200">Surface what breaks first before commitments lock in.</p>
              </div>

              <div>
                <div class="scio-text-meta scio-weight-semibold text-neutral-300">Single source of truth</div>
                <div class="mt-2 scio-text-lead scio-weight-heading text-neutral-50">
                  Integrated
                  <span :style="{ color: 'var(--color-scio-yellow-p3)' }">constraints</span>
                </div>
                <p class="mt-2 scio-text-body text-neutral-200">Scope, sequence, logistics, labour, cost and carbon.</p>
              </div>

              <div>
                <div class="scio-text-meta scio-weight-semibold text-neutral-300">Confidence</div>
                <div class="mt-2 scio-text-lead scio-weight-heading text-neutral-50">
                  Defensible
                  <span :style="{ color: 'var(--color-scio-yellow-p3)' }">choices</span>
                </div>
                <p class="mt-2 scio-text-body text-neutral-200">Traceable assumptions. Audit-ready outputs.</p>
              </div>
            </div>

          </div>

          <!-- Visual panel -->
          <div class="lg:col-span-5">
            <div class="overflow-hidden">
              <!-- Top strip (p4) -->
              <div class="pb-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <div class="scio-text-body scio-weight-semibold text-neutral-50">The Scio lens</div>
                    <p class="mt-1 scio-text-body text-neutral-200">
                      A coherent view of the programme, built for complexity.
                    </p>
                  </div>
                  <span class="scio-text-meta scio-weight-semibold"
                    :style="{ color: 'var(--color-scio-yellow-p4)' }">
                    Intelligent
                  </span>
                </div>
              </div>

              <div class="mt-5 overflow-hidden">
                <img src="/images/scio-chart-example.jpg" alt="What is Scio" class="block h-[220px] w-full object-cover opacity-95 sm:h-[280px]"
                  loading="lazy" decoding="async" />

                <!-- Caption -->
                <div class="mt-5 max-w-md">
                  <p class="scio-text-meta text-neutral-200">
                    Purpose-built for major programmes: integrate constraints early, test scenarios fast, and commit
                    with confidence.
                  </p>
                </div>
              </div>
            </div>


          </div>
        </div>

        <!-- CHALLENGE / SOLUTION -->
        <div class="mt-12 grid grid-cols-1 gap-12 bg-white/[0.02] lg:grid-cols-2 lg:gap-16">
          <article>
            <div class="h-1 w-full" :style="{ backgroundColor: 'var(--color-scio-yellow-p1)' }"></div>
            <h2 class="mt-6 scio-text-section sm:scio-text-title scio-weight-heading text-neutral-50">The challenge</h2>
            <p class="mt-5 max-w-xl scio-body-1 text-neutral-200">
              Major programmes make expensive commitments while key information is still incomplete. Disconnected
              tools hide the dependencies between scope, access, labour, materials and delivery, so the first
              constraint appears late—when change is costly.
            </p>
          </article>

          <article>
            <div class="h-1 w-full" :style="{ backgroundColor: 'var(--color-scio-yellow-p2)' }"></div>
            <h2 class="mt-6 scio-text-section sm:scio-text-title scio-weight-heading text-neutral-50">The solution</h2>
            <p class="mt-5 max-w-xl scio-body-1 text-neutral-200">
              Scio front-loads intelligence from quantities, planning assumptions, benchmarks and supply-chain
              inputs. You can test evidence-based scenarios before design, budget and contracts harden, then keep
              the same decision spine as information matures.
            </p>
          </article>
        </div>

      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section id="how-it-works" ref="howItWorksSection" class="scio-snap-section relative z-10 order-4 bg-white/[0.02]">
      <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div class="flex items-end justify-between gap-6">
          <div>
            <span class="inline-block scio-text-meta scio-weight-semibold"
              :style="{ color: 'var(--color-scio-yellow-p3)' }">
              From data to outputs
            </span>
            <h2 class="mt-4 scio-text-section sm:scio-text-title scio-weight-heading text-neutral-50">
              From programme data to decision-ready outputs.
            </h2>
            <p class="mt-3 scio-text-body sm:scio-body-1 text-neutral-200 max-w-3xl">
              Scio turns the information available today into a working model, then makes the consequences of each
              option visible before the next commitment.
            </p>
          </div>

        </div>

        <div class="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div class="space-y-1 lg:col-span-4">
            <button v-for="(step, index) in workflowSteps" :key="step.title" type="button"
              class="group w-full border-l-2 border-transparent py-6 pl-5 text-left transition"
              :class="activeWorkflow === index ? 'border-scio-yellow-p3' : 'hover:border-white/30'"
              @click="selectWorkflow(index)">
              <div class="flex items-center justify-between gap-4">
                <span class="scio-text-body scio-weight-heading text-neutral-50">0{{ index + 1 }}</span>
                <span class="scio-text-meta scio-weight-semibold" :style="{ color: step.color }">{{ step.label }}</span>
              </div>
              <div class="mt-3 scio-text-body scio-weight-heading text-neutral-50">{{ step.title }}</div>
            </button>
          </div>

          <div class="lg:col-span-8">
            <article class="min-h-[460px] overflow-hidden bg-white/[0.035] p-6 sm:p-10">
              <div class="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div class="scio-text-meta scio-weight-semibold" :style="{ color: activeWorkflowStep.color }">
                    {{ activeWorkflowStep.kicker }}
                  </div>
                  <h3 class="mt-2 scio-text-title scio-weight-heading text-neutral-50">{{ activeWorkflowStep.title }}</h3>
                  <p class="mt-3 max-w-2xl scio-body-1 text-neutral-200">{{ activeWorkflowStep.description }}</p>
                </div>
                <span class="shrink-0 scio-text-meta scio-weight-semibold"
                  :style="{ color: activeWorkflowStep.color }">{{ activeWorkflowStep.status }}</span>
              </div>

              <div class="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
                <div>
                  <div class="scio-text-meta scio-weight-semibold text-neutral-400">What goes in</div>
                  <ul class="mt-3 space-y-3">
                    <li v-for="item in activeWorkflowStep.inputs" :key="item" class="flex gap-3 scio-text-body text-neutral-200">
                      <span class="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" :style="{ backgroundColor: activeWorkflowStep.color }"></span>
                      {{ item }}
                    </li>
                  </ul>
                </div>
                <div>
                  <div class="scio-text-meta scio-weight-semibold text-neutral-400">What comes out</div>
                  <ul class="mt-3 space-y-3">
                    <li v-for="item in activeWorkflowStep.outputs" :key="item" class="flex gap-3 scio-text-body text-neutral-200">
                      <span class="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" :style="{ backgroundColor: activeWorkflowStep.color }"></span>
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>

              <div class="mt-12 max-w-2xl">
                <span class="scio-text-meta text-neutral-400">The decision it supports</span>
                <div class="mt-2 scio-text-body scio-weight-semibold text-neutral-50">{{ activeWorkflowStep.decision }}</div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <!-- SCIO DECISION STORY -->
    <section id="scio-story" class="scio-snap-section relative z-10 order-6 overflow-hidden">
      <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
        <div class="max-w-3xl">
          <span class="inline-block scio-text-meta scio-weight-semibold"
            :style="{ color: 'var(--color-scio-yellow-p4)' }">How Scio works</span>
          <h2 class="mt-3 scio-text-section sm:scio-text-title scio-weight-heading text-neutral-50">
            A decision model that gets more useful as the programme gets clearer.
          </h2>
          <p class="mt-3 max-w-2xl scio-text-body text-neutral-200">
            Scio starts with its own curated knowledge base, adds programme intent and context, then builds a model
            that can be tested, refined and used to support decisions.
          </p>
        </div>

        <div class="mt-7 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-5 lg:grid-cols-10">
          <button v-for="(step, index) in storySteps" :key="step.number" type="button"
            class="group min-h-12 text-left transition"
            :class="activeStoryIndex === index ? 'text-neutral-50' : 'text-neutral-500 hover:text-neutral-200'"
            @click="selectStoryStep(index)">
            <span class="flex items-center gap-2">
              <span class="scio-text-meta scio-weight-semibold" :style="{ color: activeStoryIndex === index ? step.color : undefined }">{{ step.number }}</span>
              <span class="h-px flex-1" :style="{ backgroundColor: activeStoryIndex === index ? step.color : 'rgba(255,255,255,0.12)' }"></span>
            </span>
            <span class="mt-2 block scio-text-meta leading-tight">{{ step.shortTitle }}</span>
          </button>
        </div>

        <transition name="story" mode="out-in">
          <article :key="activeStoryStep.number" class="mt-8 grid min-h-[390px] grid-cols-1 overflow-hidden bg-white/[0.035] lg:grid-cols-5">
            <div class="relative flex min-h-[250px] flex-col justify-between overflow-hidden bg-neutral-950/45 p-6 sm:p-8 lg:col-span-2">
              <div class="flex items-center justify-between gap-4">
                <span class="scio-text-meta scio-weight-semibold text-neutral-100">Scio model</span>
                <span class="scio-text-meta scio-weight-semibold" :style="{ color: activeStoryStep.color }">{{ activeStoryStep.stage }}</span>
              </div>

              <div class="my-8 space-y-3">
                <div v-for="(item, index) in activeStoryStep.inputs" :key="item" class="flex items-center gap-3">
                  <span class="flex h-7 w-7 shrink-0 items-center justify-center scio-text-meta scio-weight-semibold"
                    :style="{ color: activeStoryStep.color, border: `1px solid ${activeStoryStep.color}` }">0{{ index + 1 }}</span>
                  <span class="scio-text-body text-neutral-200">{{ item }}</span>
                </div>
                <div class="ml-3 h-8 w-px" :style="{ backgroundColor: activeStoryStep.color }"></div>
                <div class="flex items-center gap-3">
                  <span class="h-2 w-2 shrink-0" :style="{ backgroundColor: activeStoryStep.color }"></span>
                  <span class="scio-text-body scio-weight-semibold text-neutral-50">{{ activeStoryStep.output }}</span>
                </div>
              </div>

              <div class="flex items-center gap-3 scio-text-meta text-neutral-400">
                <span class="h-px w-8" :style="{ backgroundColor: activeStoryStep.color }"></span>
                <span>Inputs become decision evidence</span>
              </div>
            </div>

            <div class="flex min-h-[390px] flex-col p-6 sm:p-8 lg:col-span-3">
              <div class="scio-text-meta scio-weight-semibold" :style="{ color: activeStoryStep.color }">
                {{ activeStoryStep.number }} / {{ totalStorySteps }}
              </div>
              <h3 class="mt-3 scio-text-title scio-weight-heading text-neutral-50">{{ activeStoryStep.title }}</h3>
              <p class="mt-3 scio-text-body text-neutral-300">{{ activeStoryStep.description }}</p>

              <div class="mt-7 max-w-xl">
                <span class="scio-text-meta scio-weight-semibold text-neutral-400">Decision value</span>
                <p class="mt-2 scio-text-body text-neutral-200">{{ activeStoryStep.output }}</p>
              </div>

              <div class="mt-auto flex items-center justify-between pt-6">
                <button type="button" class="scio-text-meta scio-weight-semibold text-neutral-400 transition hover:text-neutral-50"
                  @click="previousStoryStep">← Previous</button>
                <button type="button" class="scio-text-meta scio-weight-semibold transition hover:text-neutral-50"
                  :style="{ color: activeStoryStep.color }" @click="nextStoryStep">Next →</button>
              </div>
            </div>
          </article>
        </transition>
      </div>
    </section>

    <!-- SHORT-FORM VALUE STATEMENTS -->
    <section id="impact" class="scio-snap-section relative z-10 order-2">
      <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div class="max-w-3xl">
          <span class="inline-block scio-text-meta scio-weight-semibold"
            :style="{ color: 'var(--color-scio-yellow-p2)' }">Why it matters</span>
          <h2 class="mt-4 scio-text-section sm:scio-text-title scio-weight-heading text-neutral-50">
            See earlier. Choose better. Commit stronger.
          </h2>
        </div>

        <div class="mt-14 grid grid-cols-1 gap-3 md:grid-cols-3">
          <button v-for="(benefit, index) in benefits" :key="benefit.title" type="button"
            class="group relative min-h-[330px] overflow-hidden p-7 text-left transition sm:p-9"
            :class="activeImpact === index ? 'bg-white/[0.06]' : 'bg-white/[0.02] hover:bg-white/[0.04]'"
            @mouseenter="selectImpact(index)" @focus="selectImpact(index)">
            <span class="scio-text-data scio-weight-semibold" :style="{ color: benefit.color }">0{{ index + 1 }}</span>
            <div class="mt-16 scio-text-meta scio-weight-semibold" :style="{ color: benefit.color }">{{ benefit.label }}</div>
            <h3 class="mt-4 scio-text-title scio-weight-heading text-neutral-50">{{ benefit.title }}</h3>
            <p class="mt-3 max-w-sm scio-text-body text-neutral-300">{{ benefit.body }}</p>
            <span class="absolute bottom-7 right-7 scio-text-meta text-neutral-500 transition group-hover:text-neutral-200 sm:bottom-9 sm:right-9">↗</span>
          </button>
        </div>
      </div>
    </section>

    <!-- TESTIMONIALS -->
    <section class="scio-snap-section relative z-10 order-5">
      <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div class="flex items-end justify-between gap-6">
          <div>
            <span class="inline-block scio-text-meta scio-weight-semibold"
              :style="{ color: 'var(--color-scio-yellow-p2)' }">
              Proof
            </span>
            <h2 class="mt-4 scio-text-section sm:scio-text-title scio-weight-heading text-neutral-50">
            What programme leaders say
          </h2>
          <p class="mt-3 scio-text-body sm:scio-body-1 text-neutral-200 max-w-3xl">
              Perspectives from Jacobs programme directors and executives who have used Scio on major programmes.
            </p>
          </div>

        </div>

        <div class="relative mt-14 flex h-[460px] min-h-0 flex-col overflow-hidden bg-white/[0.03] px-6 py-10 sm:h-[340px] sm:px-10 lg:h-[320px]" @mouseenter="pauseTestimonialCarousel"
          @mouseleave="startTestimonialCarousel" aria-live="polite">
          <transition name="quote" mode="out-in">
            <article :key="activeTestimonial.name" class="flex h-full min-h-0 flex-col pt-8">
          <div class="max-w-5xl min-h-0 flex-1 pr-32 scio-body-1 sm:scio-text-lead font-normal leading-relaxed text-neutral-200">
                “{{ activeTestimonial.quote }}”
              </div>
              <div class="mt-7 flex shrink-0 flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <div class="scio-text-body scio-weight-semibold text-neutral-50">{{ activeTestimonial.name }}</div>
                  <div class="scio-text-meta text-neutral-400">{{ activeTestimonial.title }}</div>
                </div>
                <div class="scio-text-meta text-neutral-400">{{ currentSlide + 1 }} / {{ totalItems }}</div>
            </div>
            </article>
          </transition>
          <div class="absolute left-6 right-6 top-10 flex items-center gap-5 sm:left-10 sm:right-10">
            <div class="h-px flex-1 bg-white/10">
              <div class="h-px transition-all duration-300" :style="{ width: `${((currentSlide + 1) / totalItems) * 100}%`, background: 'var(--color-scio-yellow-p2)' }"></div>
            </div>
            <button type="button" class="scio-text-meta scio-weight-semibold text-neutral-400 transition hover:text-neutral-50"
              @click="nextTestimonial">
              Next proof →
            </button>
          </div>
        </div>

      </div>
    </section>

    <!-- WHO IS SCIO FOR + CAPABILITIES -->
    <section id="capabilities" class="scio-snap-section relative z-10 order-3 bg-white/[0.02]">
      <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <span class="inline-block scio-text-meta scio-weight-semibold"
          :style="{ color: 'var(--color-scio-yellow-p4)' }">
          Who it’s for
        </span>

        <h2 class="mt-4 scio-text-section sm:scio-text-title scio-weight-heading text-neutral-50">
          Built for the people who have to make the next programme decision.
        </h2>

        <p class="mt-4 scio-text-body sm:scio-body-1 text-neutral-200 max-w-3xl">
          Scio gives programme directors, owners, PMOs and digital delivery teams a shared decision view—so each group
          can challenge the same assumptions and act on the same evidence.
        </p>

        <div class="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div class="lg:col-span-4">
            <button v-for="(track, index) in audienceTracks" :key="track.role" type="button"
              class="group w-full border-l-2 border-transparent py-6 pl-5 text-left transition"
              :class="activeAudience === index ? 'border-scio-yellow-p3' : 'hover:border-white/30'"
              @click="selectAudience(index)">
              <div class="flex items-center justify-between gap-4">
                <span class="scio-text-meta scio-weight-semibold" :style="{ color: track.color }">0{{ index + 1 }}</span>
              </div>
              <div class="mt-3 scio-text-body scio-weight-heading text-neutral-50">{{ track.role }}</div>
              <div class="mt-2 scio-text-body text-neutral-300">{{ track.summary }}</div>
            </button>
          </div>

          <div class="bg-white/[0.035] p-6 sm:p-10 lg:col-span-8">
            <div class="scio-text-meta scio-weight-semibold" :style="{ color: activeAudienceTrack.color }">
              {{ activeAudienceTrack.kicker }}
            </div>
            <h3 class="mt-3 scio-text-title scio-weight-heading text-neutral-50">{{ activeAudienceTrack.title }}</h3>
            <p class="mt-3 max-w-3xl scio-body-1 text-neutral-200">{{ activeAudienceTrack.description }}</p>

            <div class="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <div class="scio-text-meta scio-weight-semibold text-neutral-400">Questions Scio helps answer</div>
                <ul class="mt-4 space-y-3">
                  <li v-for="item in activeAudienceTrack.questions" :key="item" class="flex gap-3 scio-text-body text-neutral-200">
                    <span class="mt-2 h-1.5 w-1.5 shrink-0" :style="{ backgroundColor: activeAudienceTrack.color }"></span>
                    {{ item }}
                  </li>
                </ul>
              </div>
              <div>
                <div class="scio-text-meta scio-weight-semibold text-neutral-400">What Scio gives you</div>
                <ul class="mt-4 space-y-3">
                  <li v-for="item in activeAudienceTrack.outputs" :key="item" class="flex gap-3 scio-text-body text-neutral-200">
                    <span class="mt-2 h-1.5 w-1.5 shrink-0" :style="{ backgroundColor: activeAudienceTrack.color }"></span>
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>

            <div class="mt-8 border-t border-white/10 pt-4">
              <span class="scio-text-meta text-neutral-400">Best used at:</span>
              <span class="ml-2 scio-text-body scio-weight-semibold text-neutral-50">{{ activeAudienceTrack.when }}</span>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- CLIENT READINESS -->
    <section id="security" class="scio-snap-section relative z-10 order-7 bg-white/[0.025]">
      <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12">
          <div class="lg:col-span-2">
            <span class="inline-block scio-text-meta scio-weight-semibold"
              :style="{ color: 'var(--color-scio-yellow-p4)' }">Client readiness</span>
            <h2 class="mt-4 scio-text-section sm:scio-text-title scio-weight-heading text-neutral-50">
              The questions your security team will ask.
            </h2>
            <p class="mt-4 scio-body-1 text-neutral-200">
              Scio is an authenticated internal application for programme decision-making. It stores structured
              programme data, applies role and project access controls, and produces traceable scenario outputs.
              Client security and privacy requirements are agreed before programme data is connected.
            </p>
          </div>

          <div class="space-y-3 lg:col-span-3">
            <div v-for="(question, index) in securityQuestions" :key="question.question"
              class="overflow-hidden border-b border-white/10">
              <button type="button" class="flex w-full items-center justify-between gap-4 py-5 text-left transition hover:text-neutral-300"
                @click="toggleSecurityQuestion(index)">
                <span class="scio-text-body scio-weight-semibold text-neutral-50">{{ question.question }}</span>
                <span class="scio-text-title text-neutral-300">{{ activeSecurityQuestion === index ? '−' : '+' }}</span>
              </button>
              <div v-if="activeSecurityQuestion === index" class="pb-5">
                <p class="scio-text-body text-neutral-300">{{ question.answer }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-16 pt-6 scio-text-meta text-neutral-200">
          <span class="mr-2">© {{ new Date().getFullYear() }} Scio</span>
          <span class="inline-block h-1 w-1 rounded-full bg-neutral-500 align-middle mx-2"></span>
          <span>Clarity before commitment</span>
        </div>
      </div>
    </section>
  </PublicPageShell>
</template>

<script>
import PublicPageShell from "@/common/components/layout/PublicPageShell.vue";

export default {
  name: "WhyScio",
  components: { PublicPageShell },
  
  data() {
    return {
      activeWorkflow: 0,
      activeStoryIndex: 0,
      activeAudience: 0,
      activeImpact: 0,
      activeSecurityQuestion: 0,
      workflowSteps: [
        {
          label: "Connect",
          kicker: "01 · Bring the programme together",
          title: "Start with the information you have",
          description: "Scio creates a coherent structure from early quantities, schedule logic, benchmarks, assumptions and delivery constraints. It does not wait for a finished design.",
          status: "Inputs",
          progress: 25,
          color: "var(--color-scio-yellow-p1)",
          gradient: "linear-gradient(90deg, var(--color-scio-yellow-p1), var(--color-scio-yellow-p2))",
          inputs: ["Scope and quantities", "Programme phases and rates", "Client requirements and constraints"],
          outputs: ["A shared baseline", "Explicit assumptions", "A traceable decision spine"],
          decision: "What are we actually planning to deliver?",
        },
        {
          label: "Model",
          kicker: "02 · Make the logic visible",
          title: "Turn assumptions into a working model",
          description: "Domain expertise and predictive logic connect materials, labour, plant, access, logistics, cost and carbon so the dependencies are visible together.",
          status: "Logic",
          progress: 50,
          color: "var(--color-scio-yellow-p2)",
          gradient: "linear-gradient(90deg, var(--color-scio-yellow-p2), var(--color-scio-yellow-p3))",
          inputs: ["Production and productivity rates", "Transport, access and capacity rules", "Cost and carbon factors"],
          outputs: ["Time-phased demand", "Constraint and interface view", "Traceable calculations"],
          decision: "What must be true for the plan to work?",
        },
        {
          label: "Simulate",
          kicker: "03 · Compare realistic options",
          title: "Test what changes the outcome",
          description: "Branch the decisions that matter and compare delivery options against the same baseline—without hiding the trade-offs between time, cost, carbon and feasibility.",
          status: "Scenarios",
          progress: 75,
          color: "var(--color-scio-yellow-p3)",
          gradient: "linear-gradient(90deg, var(--color-scio-yellow-p3), var(--color-scio-yellow-p4))",
          inputs: ["Alternative sequences and methods", "Material and logistics choices", "Enabling works and mitigation levers"],
          outputs: ["Scenario deltas", "Bottleneck and failure windows", "Implications by discipline"],
          decision: "Which option is genuinely buildable?",
        },
        {
          label: "Decide",
          kicker: "04 · Make the commitment defensible",
          title: "Move forward with evidence",
          description: "Scio turns the comparison into a decision-ready view that can be challenged, governed and refreshed as information matures.",
          status: "Decision",
          progress: 100,
          color: "var(--color-scio-yellow-p4)",
          gradient: "linear-gradient(90deg, var(--color-scio-yellow-p4), var(--color-scio-yellow-p2))",
          inputs: ["Preferred scenario and sensitivities", "Residual risks and conditions", "Decision owners and governance needs"],
          outputs: ["Recommended path", "Must-be-true conditions", "Actions to protect the plan"],
          decision: "What should we commit to—and what do we monitor?",
        },
      ],
      storySteps: [
        {
          number: "01",
          shortTitle: "Existing data",
          stage: "Scio knowledge base",
          title: "Start with Scio’s own data source.",
          description: "Scio brings together quantities, assemblies, activities, materials, labour, equipment, carbon, costs and energy from decades of SME experience and trusted third-party sources, including the ICE carbon database.",
          inputs: ["Quantities, assemblies and activities", "Materials, labour, equipment, carbon, cost and energy"],
          output: "A reusable evidence base ready to be tailored to the programme.",
          color: "var(--color-scio-yellow-p1)",
        },
        {
          number: "02",
          shortTitle: "Intent and inputs",
          stage: "Define the programme",
          title: "Tell Scio what is being built.",
          description: "We need to understand what is being built and its approximate size. GFA, masterplans and other early design information are especially helpful for interpreting the programme and tailoring the model.",
          inputs: ["Asset types and approximate sizes", "GFA, masterplans or early design information"],
          output: "A programme-specific interpretation of scope and scale.",
          color: "var(--color-scio-yellow-p2)",
        },
        {
          number: "03",
          shortTitle: "Baseline",
          stage: "Define the reference point",
          title: "Define the baseline.",
          description: "Agree the reference case before testing alternatives, with every important assumption explicit and traceable.",
          inputs: ["Scope and delivery assumptions", "Known constraints and dependencies"],
          output: "A common baseline against which every scenario can be compared.",
          color: "var(--color-scio-yellow-p3)",
        },
        {
          number: "04",
          shortTitle: "Activities",
          stage: "Structure the work",
          title: "Translate scope into activities.",
          description: "Break the programme into the activities, interfaces and enabling works that drive delivery demand.",
          inputs: ["Work packages and methods", "Interfaces, access and enabling works"],
          output: "A delivery structure that can be calculated, tested and refined.",
          color: "var(--color-scio-yellow-p4)",
        },
        {
          number: "05",
          shortTitle: "Demand",
          stage: "Calculate requirements",
          title: "Calculate demand.",
          description: "Apply production logic to estimate the materials, labour, plant and services required to deliver the activities.",
          inputs: ["Quantities and productivity rates", "Resource and capacity assumptions"],
          output: "A quantified view of demand, constraints and resource peaks.",
          color: "var(--color-scio-yellow-p1)",
        },
        {
          number: "06",
          shortTitle: "Time",
          stage: "Phase the assets",
          title: "Add when things get built.",
          description: "We phase the assets to show when they are built. A P6 schedule is helpful and can be ingested when available, but it is not essential. Scio can work with an immature schedule and fill the gaps as the programme develops.",
          inputs: ["Asset phasing and build sequence", "P6 schedule or planning information, if available"],
          output: "A time-phased model that becomes richer as the schedule matures.",
          color: "var(--color-scio-yellow-p2)",
        },
        {
          number: "07",
          shortTitle: "Logistics",
          stage: "Model movement and access",
          title: "Model logistics.",
          description: "Connect demand to the movement, access, storage and capacity conditions that determine whether delivery can flow.",
          inputs: ["Delivery routes and access rules", "Storage, traffic and site capacity"],
          output: "A logistics view that exposes congestion, conflicts and mitigation levers.",
          color: "var(--color-scio-yellow-p3)",
        },
        {
          number: "08",
          shortTitle: "Factors",
          stage: "Apply the evidence",
          title: "Apply the factors that change the outcome.",
          description: "Use curated knowledge, project factors and client-defined rules to move from a simple estimate to a credible programme view.",
          inputs: ["Cost, carbon and productivity factors", "Project, market and location adjustments"],
          output: "Traceable calculations grounded in the conditions of the programme.",
          color: "var(--color-scio-yellow-p4)",
        },
        {
          number: "09",
          shortTitle: "Scenarios",
          stage: "Test the choices",
          title: "Run scenario analysis.",
          description: "Change the levers that matter and compare realistic options against the same baseline.",
          inputs: ["Alternative sequences and methods", "Mitigation levers and sensitivities"],
          output: "Clear trade-offs across time, cost, carbon, logistics and feasibility.",
          color: "var(--color-scio-yellow-p1)",
        },
        {
          number: "10",
          shortTitle: "Scio outputs",
          stage: "Make the decision",
          title: "Turn the model into Scio outputs.",
          description: "Bring the evidence together in decision-ready outputs that can be challenged, governed and refreshed as the programme changes.",
          inputs: ["Preferred scenario and sensitivities", "Decision owners and conditions"],
          output: "A defensible recommendation with the assumptions, risks and actions needed to protect it.",
          color: "var(--color-scio-yellow-p2)",
        },
      ],
      benefits: [
        { label: "SEE EARLIER", title: "The risk before the consequence", body: "Find the first constraint, where it binds and when it becomes expensive to change.", color: "var(--color-scio-yellow-p1)" },
        { label: "CHOOSE BETTER", title: "The option that works in practice", body: "Compare delivery, cost, carbon and logistics together instead of optimising one in isolation.", color: "var(--color-scio-yellow-p2)" },
        { label: "COMMIT STRONGER", title: "A decision that survives challenge", body: "Make the assumptions, sensitivities and conditions explicit for governance and delivery teams.", color: "var(--color-scio-yellow-p3)" },
      ],
      audienceTracks: [
        {
          role: "Programme directors",
          summary: "Set a credible ambition before the plan hardens.",
          kicker: "01 · Set direction",
          title: "Know what must be true before you commit.",
          description: "Scio gives programme leaders an early view of feasibility, dependencies and failure points—so the strategic conversation starts with evidence rather than optimism.",
          questions: ["Is the target achievable with the current constraints?", "What breaks first if the plan changes?", "What conditions must be protected?"],
          outputs: ["Feasibility baseline", "Scenario comparison", "Decision recommendation"],
          timing: "Masterplanning · feasibility · concept",
          when: "Masterplanning, feasibility and concept",
          color: "var(--color-scio-yellow-p1)",
        },
        {
          role: "Owners and sponsors",
          summary: "Protect the investment decision from avoidable surprises.",
          kicker: "02 · Protect value",
          title: "See the consequences of the decision you are making.",
          description: "Scio connects delivery choices to cost, carbon, logistics and programme risk, giving sponsors a clearer basis for funding, phasing and change decisions.",
          questions: ["What are we committing to—and what remains uncertain?", "Which choices move cost, time or carbon?", "Which enabling works are unavoidable?"],
          outputs: ["Cost and carbon implications", "Option trade-offs", "Governance-ready evidence"],
          timing: "Business case · investment · change",
          when: "Business case, investment and change",
          color: "var(--color-scio-yellow-p2)",
        },
        {
          role: "PMO and controls",
          summary: "Keep the baseline honest as the programme evolves.",
          kicker: "03 · Maintain control",
          title: "Turn change into a visible decision, not a late surprise.",
          description: "Scio preserves the assumptions behind the plan and shows how a change moves through the system, helping controls teams focus escalation where it matters most.",
          questions: ["Where is the programme drifting from its baseline?", "Which dependency is binding the next decision?", "What needs escalation now?"],
          outputs: ["Traceable assumptions", "Constraint and dependency view", "Refreshed decision outputs"],
          timing: "Planning · procurement · delivery",
          when: "Planning, procurement and delivery",
          color: "var(--color-scio-yellow-p3)",
        },
        {
          role: "Digital and data teams",
          summary: "Make fragmented programme data usable and reusable.",
          kicker: "04 · Enable decisions",
          title: "Create one structured model that improves with the programme.",
          description: "Scio turns early, incomplete information into a consistent model that can be governed, extended and reused as the programme data matures.",
          questions: ["What inputs are required to start?", "How do we keep a common model across teams?", "Can the logic and outputs be reused?"],
          outputs: ["Structured programme model", "Reusable scenarios", "Auditable outputs"],
          timing: "Setup · integration · lifecycle",
          when: "Setup, integration and lifecycle",
          color: "var(--color-scio-yellow-p4)",
        },
      ],
      securityQuestions: [
        {
          question: "What information does Scio store?",
          answer: "Scio stores structured programme information: scope, quantities, schedules, locations, materials, logistics, labour demand, cost, carbon, assumptions, scenarios and decision outputs. It does not require personal data or a completed BIM/CAD model to start.",
        },
        {
          question: "Where is Scio hosted and where is data stored?",
          answer: "The current Scio deployment uses Azure Static Web Apps for the frontend, Azure App Service for the API and a SQL Server database for application and programme data. Users authenticate through Microsoft Entra ID. The hosting tenant, region, retention and backup arrangements are confirmed against each client’s requirements before data is connected.",
        },
        {
          question: "Who can access our programme data?",
          answer: "Scio access is authenticated through Microsoft Entra ID and controlled by role-based and project-level permissions. Users only receive access to the programmes and functions assigned to them. API access is protected by the application’s session, CSRF and route-level access controls.",
        },
        {
          question: "Does Scio process personal or GDPR data?",
          answer: "Not by design. Scio is built for programme, asset, material, logistics, cost, carbon and workforce-demand data—not personal records. Personal or special-category data must not be connected unless it is necessary, classified, approved and covered by the client’s privacy requirements. The PIA/DPIA is updated if the confirmed data inventory identifies personal data. We have no known use case of this but are prepared for it becoming a possibility in the future.",
        },
        {
          question: "How does Scio meet data-sovereignty requirements?",
          answer: "Hosting location, tenant, cross-border access, user roles, integrations, retention and deletion are defined as deployment requirements. These are agreed with the client’s security, legal and information-governance teams before programme data is connected.",
        },
        {
          question: "What assurance documents are available before data access?",
          answer: "The Technical and Organisational Measures (TOM) and Privacy Impact Assessment/Data Protection Impact Assessment (PIA/DPIA) will be ready before client data is accessed. The TOM covers Scio’s security and operational controls. The PIA/DPIA covers the confirmed data inventory, flows, privacy risks, lawful basis, retention, data-subject rights, processors, transfers and mitigations. Both are updated after onboarding when the actual data types and flows are confirmed.",
        },
        {
          question: "Can Scio decisions and outputs be audited?",
          answer: "Yes. Scio keeps assumptions, factors, scenarios, changes and outputs traceable so a decision can be reviewed, challenged and refreshed as the programme changes.",
        },
      ],
      currentSlide: 0,
      testimonialTimer: null,
      contentArea: null,
      pageScrollLocked: false,
      pageScrollTimer: null,
      testimonialItems: [
        { type: "image", src: "/images/obraz.jpg" },
        {
          type: "text",
          quote:
            "Scio represents a true step-change in program management, enabling us to make strategic decisions at the earliest stages of project planning. On KSIA, Scio has allowed our teams to anticipate challenges and opportunities well in advance, fostering proactive rather than reactive behaviours. It is an indispensable tool in today’s dynamic project management landscape.",
          name: "Suzanne McCormack",
          title: "Vice President | Executive Programme Director",
          shortTitle: "Earlier strategic decisions",
        },
        {
          type: "text",
          quote:
            "Scio has fundamentally changed how we approach aviation programme planning. By connecting data from the outset, we can model risk, cost, logistics, and sustainability in real-time - long before traditional tools even come into play. It's become a core part of how we drive smarter, faster decision-making across our global aviation portfolio.",
          name: "Andrew Gibson",
          title: "Vice President | Global Aviation Market Director",
          shortTitle: "Connected planning from day one",
        },
        {
          type: "text",
          quote:
            "In over 30 years working on major infrastructure programs — from early master planning through to construction — I’ve seen countless tools promise clarity and control. Scio is the first that truly delivers. What sets it apart is the depth and readiness of its data from day one. We’re no longer waiting for detailed designs to start making strategic decisions — Scio provides immediate visibility into materials, labour, logistics, costs, and risk, all grounded in a robust, curated knowledge base of construction, design, and logistics data.",
          name: "Marcio A da. Matta",
          title: "VP Infrastructure & Mobility, KSIADC",
          shortTitle: "Visibility before detailed design",
        },
      ],

      capabilities: [
        {
          img: "/images/iStock-2149785775.webp",
          title: "Intelligent Material Management",
          tag: "Materials",
          accent: "var(--color-scio-yellow-p1)",
          bar: "linear-gradient(90deg, var(--color-scio-yellow-p1), var(--color-scio-yellow-p2))",
          desc:
            "Predict material needs and delivery schedules earlier than traditional workflows—reducing procurement risk and improving readiness from day one.",
        },
        {
          img: "/images/iStock-1357436749.webp",
          title: "Data-Driven CapEx Projection",
          tag: "Cost",
          accent: "var(--color-scio-yellow-p2)",
          bar: "linear-gradient(90deg, var(--color-scio-yellow-p2), var(--color-scio-yellow-p3))",
          desc:
            "Combine early design, logistics, labour and market data to produce dynamic CapEx forecasts and defensible baselines for planning.",
        },
        {
          img: "/images/iStock-1282716872.webp",
          title: "Holistic Carbon Analysis",
          tag: "Carbon",
          accent: "var(--color-scio-yellow-p3)",
          bar: "linear-gradient(90deg, var(--color-scio-yellow-p3), var(--color-scio-yellow-p4))",
          desc:
            "Model carbon impact from the outset across materials, logistics, traffic and enabling resources—supporting credible sustainability baselines.",
        },
        {
          img: "/images/iStock-1335697420.webp",
          title: "Logistics Planning & Optimisation",
          tag: "Logistics",
          accent: "var(--color-scio-yellow-p4)",
          bar: "linear-gradient(90deg, var(--color-scio-yellow-p4), var(--color-scio-yellow-p2))",
          desc:
            "Model the demand and flow of materials, labour and equipment across time—reducing congestion and aligning deliveries with site readiness.",
        },
        {
          img: "/images/iStock-1465416774.webp",
          title: "Construction Traffic Modelling",
          tag: "Traffic",
          accent: "var(--color-scio-yellow-p1)",
          bar: "linear-gradient(90deg, var(--color-scio-yellow-p1), var(--color-scio-yellow-p3))",
          desc:
            "Forecast traffic volumes from logistics plans and site constraints to assess network impacts, access strategies and stakeholder disruption.",
        },
        {
          img: "/images/iStock-473326760.webp",
          title: "Ancillary Demand Forecasting",
          tag: "Enabling",
          accent: "var(--color-scio-yellow-p2)",
          bar: "linear-gradient(90deg, var(--color-scio-yellow-p2), var(--color-scio-yellow-p4))",
          desc:
            "Estimate demand for power, water, fuel and welfare by phase—enabling smoother site operations and better enabling works decisions.",
        },
        {
          img: "/images/iStock-1189899412.webp",
          title: "Predictive Resource Planning",
          tag: "Labour",
          accent: "var(--color-scio-yellow-p3)",
          bar: "linear-gradient(90deg, var(--color-scio-yellow-p3), var(--color-scio-yellow-p1))",
          desc:
            "Use schedule logic and materials forecasts to predict workforce and plant needs—flattening peaks and aligning resources to targets.",
        },
        {
          img: "/images/iStock-179985599.webp",
          title: "Lifecycle Delivery Intelligence",
          tag: "Delivery",
          accent: "var(--color-scio-yellow-p4)",
          bar: "linear-gradient(90deg, var(--color-scio-yellow-p4), var(--color-scio-yellow-p1))",
          desc:
            "Carry a consistent, traceable view from business case to handover—so decisions stay aligned as delivery reality changes.",
        },
      ],
    };
  },
  mounted() {
    this.contentArea = document.querySelector(".content-area");
    this.contentArea?.classList.add("scio-snap-page");
    this.contentArea?.addEventListener("wheel", this.handleSectionWheel, { passive: false });
    this.startTestimonialCarousel();
  },
  beforeUnmount() {
    this.contentArea?.removeEventListener("wheel", this.handleSectionWheel);
    if (this.pageScrollTimer) window.clearTimeout(this.pageScrollTimer);
    this.contentArea?.classList.remove("scio-snap-page");
    this.pauseTestimonialCarousel();
  },
  computed: {
    activeWorkflowStep() {
      return this.workflowSteps[this.activeWorkflow] || this.workflowSteps[0];
    },
    activeStoryStep() {
      return this.storySteps[this.activeStoryIndex] || this.storySteps[0];
    },
    totalStorySteps() {
      return String(this.storySteps.length).padStart(2, "0");
    },
    activeAudienceTrack() {
      return this.audienceTracks[this.activeAudience] || this.audienceTracks[0];
    },
    customerTestimonials() {
      return this.testimonialItems.filter((item) => item.type === "text");
    },
    activeTestimonial() {
      return this.customerTestimonials[this.currentSlide] || this.customerTestimonials[0];
    },
    totalItems() {
      return this.customerTestimonials.length;
    },
  },
  methods: {
    startTestimonialCarousel() {
      this.pauseTestimonialCarousel();
      this.testimonialTimer = window.setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.totalItems;
      }, 12000);
    },
    pauseTestimonialCarousel() {
      if (this.testimonialTimer) {
        window.clearInterval(this.testimonialTimer);
        this.testimonialTimer = null;
      }
    },
    nextTestimonial() {
      this.currentSlide = (this.currentSlide + 1) % this.totalItems;
      this.startTestimonialCarousel();
    },
    selectWorkflow(index) {
      this.activeWorkflow = index;
    },
    selectStoryStep(index) {
      this.activeStoryIndex = index;
    },
    previousStoryStep() {
      this.activeStoryIndex = (this.activeStoryIndex - 1 + this.storySteps.length) % this.storySteps.length;
    },
    nextStoryStep() {
      this.activeStoryIndex = (this.activeStoryIndex + 1) % this.storySteps.length;
    },
    handleSectionWheel(event) {
      if (!this.contentArea || Math.abs(event.deltaY) < 8) return;

      if (this.pageScrollLocked) {
        event.preventDefault();
        return;
      }

      const nestedScroll = event.target?.closest?.("[data-scio-scroll]");
      if (nestedScroll) {
        const canScrollDown = nestedScroll.scrollTop + nestedScroll.clientHeight < nestedScroll.scrollHeight - 1;
        const canScrollUp = nestedScroll.scrollTop > 0;
        if ((event.deltaY > 0 && canScrollDown) || (event.deltaY < 0 && canScrollUp)) return;
      }

      const sections = Array.from(this.contentArea.querySelectorAll(".scio-snap-section")).sort((first, second) => {
        return Number(window.getComputedStyle(first).order) - Number(window.getComputedStyle(second).order);
      });
      if (!sections.length) return;

      const containerTop = this.contentArea.getBoundingClientRect().top;
      const currentIndex = sections.reduce((closestIndex, section, index) => {
        const closestDistance = Math.abs(sections[closestIndex].getBoundingClientRect().top - containerTop);
        const sectionDistance = Math.abs(section.getBoundingClientRect().top - containerTop);
        return sectionDistance < closestDistance ? index : closestIndex;
      }, 0);
      const nextIndex = currentIndex + (event.deltaY > 0 ? 1 : -1);

      if (nextIndex < 0 || nextIndex >= sections.length) return;

      event.preventDefault();
      const nextSection = sections[nextIndex];
      const top = nextSection.getBoundingClientRect().top - containerTop + this.contentArea.scrollTop;
      this.pageScrollLocked = true;
      this.contentArea.scrollTo({ top, behavior: "smooth" });
      this.pageScrollTimer = window.setTimeout(() => {
        this.pageScrollLocked = false;
        this.pageScrollTimer = null;
      }, 700);
    },
    selectAudience(index) {
      this.activeAudience = index;
    },
    selectImpact(index) {
      this.activeImpact = index;
    },
    toggleSecurityQuestion(index) {
      this.activeSecurityQuestion = this.activeSecurityQuestion === index ? -1 : index;
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

  },
};
</script>

<style scoped>
.scio-snap-section {
  box-sizing: border-box;
  display: flex;
  min-height: calc(100dvh - var(--topbar-height));
  flex-direction: column;
  justify-content: center;
}

.scio-snap-section > .mx-auto {
  width: 100%;
  padding-block: clamp(2.5rem, 5vh, 4rem);
}

.scio-snap-hero {
  min-height: 0;
  justify-content: flex-start;
}

.quote-enter-active,
.quote-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.quote-enter-from {
  opacity: 0;
  transform: translateY(0.5rem);
}

.quote-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

.story-enter-active,
.story-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.story-enter-from {
  opacity: 0;
  transform: translateY(0.5rem);
}

.story-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

</style>
